import { formatBRL } from './format';

/**
 * Parses numeric inputs handling both dot and comma decimal separators.
 */
export const parseNumeric = (val, defaultValue = 0) => {
  if (val === null || val === undefined || val === '') return defaultValue;
  if (typeof val === 'number') return isNaN(val) ? defaultValue : val;
  const cleaned = String(val).replace(/\s/g, '').replace(',', '.');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Parses a YYYY-MM-DD date string at midnight local time to avoid timezone offset issues.
 */
const parseLocalDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const parts = dateStr.split('-');
  if (parts.length < 3) return null;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  return new Date(year, month, day);
};

/**
 * Calculates interest, discount and final price for a financial record or form data.
 */
export const calculateRecordPriceDetails = (record) => {
  if (!record) {
    return {
      baseValue: 0,
      finalValue: 0,
      diffDays: 0,
      adjustmentType: 'none',
      adjustmentAmount: 0,
      appliedRatePct: 0,
      hasAdjustment: false,
      statusMessage: 'Sem dados para cálculo',
      badgeText: null,
      tooltip: ''
    };
  }

  const baseValue = parseNumeric(record.value, 0);
  if (baseValue <= 0 || !record.due_date) {
    return {
      baseValue,
      finalValue: baseValue,
      diffDays: 0,
      adjustmentType: 'none',
      adjustmentAmount: 0,
      appliedRatePct: 0,
      hasAdjustment: false,
      statusMessage: 'Sem juros ou descontos aplicados',
      badgeText: null,
      tooltip: `Valor: ${formatBRL(baseValue)}`
    };
  }

  const dueDate = parseLocalDate(record.due_date);
  if (!dueDate) {
    return {
      baseValue,
      finalValue: baseValue,
      diffDays: 0,
      adjustmentType: 'none',
      adjustmentAmount: 0,
      appliedRatePct: 0,
      hasAdjustment: false,
      statusMessage: 'Data de vencimento inválida',
      badgeText: null,
      tooltip: `Valor: ${formatBRL(baseValue)}`
    };
  }

  // Determine effective comparison date
  let targetDate;
  if (record.is_paid && record.payment_date) {
    targetDate = parseLocalDate(record.payment_date);
  }
  if (!targetDate) {
    // If unpaid or payment_date not specified, compare against today (midnight local time)
    const now = new Date();
    targetDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  // Calculate day difference
  const diffTime = targetDate.getTime() - dueDate.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  const enableInterest = Boolean(record.enable_interest);
  const interestDailyRate = parseNumeric(record.interest_daily_rate, 0);
  const maxInterestRate = parseNumeric(record.max_interest_rate, 0);

  const enableEarlyDiscount = Boolean(record.enable_early_discount);
  const earlyDiscountDailyRate = parseNumeric(record.early_discount_daily_rate, 0);

  // Late payment -> Apply interest if enabled
  if (diffDays > 0 && enableInterest && interestDailyRate > 0) {
    const rawPct = diffDays * interestDailyRate;
    const appliedRatePct = maxInterestRate > 0 ? Math.min(rawPct, maxInterestRate) : rawPct;
    const interestAmount = baseValue * (appliedRatePct / 100);
    const finalValue = Math.round((baseValue + interestAmount) * 100) / 100;

    const isCapped = maxInterestRate > 0 && rawPct > maxInterestRate;
    const capInfo = isCapped ? ` (Limitado ao teto de ${maxInterestRate.toFixed(2).replace('.', ',')}%)` : '';

    return {
      baseValue,
      finalValue,
      diffDays,
      adjustmentType: 'interest',
      adjustmentAmount: interestAmount,
      appliedRatePct,
      hasAdjustment: interestAmount > 0,
      statusMessage: `${diffDays} dia${diffDays > 1 ? 's' : ''} de atraso (+${appliedRatePct.toFixed(3).replace('.', ',')}% juros)${capInfo}`,
      badgeText: `+${formatBRL(interestAmount)} juros`,
      tooltip: `Valor Original: ${formatBRL(baseValue)} | Juros: +${formatBRL(interestAmount)} (${diffDays} dias de atraso a ${interestDailyRate}%/dia${capInfo}) | Preço Final: ${formatBRL(finalValue)}`
    };
  }

  // Early payment -> Apply early payment discount if enabled
  if (diffDays < 0 && enableEarlyDiscount && earlyDiscountDailyRate > 0) {
    const daysEarly = Math.abs(diffDays);
    const rawPct = daysEarly * earlyDiscountDailyRate;
    const appliedRatePct = Math.min(rawPct, 100);
    const discountAmount = baseValue * (appliedRatePct / 100);
    const finalValue = Math.round(Math.max(0, baseValue - discountAmount) * 100) / 100;

    return {
      baseValue,
      finalValue,
      diffDays,
      adjustmentType: 'discount',
      adjustmentAmount: discountAmount,
      appliedRatePct,
      hasAdjustment: discountAmount > 0,
      statusMessage: `${daysEarly} dia${daysEarly > 1 ? 's' : ''} de antecedência (-${appliedRatePct.toFixed(3).replace('.', ',')}% desc.)`,
      badgeText: `-${formatBRL(discountAmount)} desc.`,
      tooltip: `Valor Original: ${formatBRL(baseValue)} | Desconto: -${formatBRL(discountAmount)} (${daysEarly} dias antecipados a ${earlyDiscountDailyRate}%/dia) | Preço Final: ${formatBRL(finalValue)}`
    };
  }

  // On time or no rules triggered
  let statusMessage = 'Em dia / Sem juros ou desconto';
  if (enableInterest && diffDays <= 0) {
    statusMessage = 'Cálculo de juros ativo (sem atraso no momento)';
  }
  if (enableEarlyDiscount && diffDays >= 0) {
    statusMessage = 'Cálculo de desconto ativo (sem antecipação no momento)';
  }

  return {
    baseValue,
    finalValue: baseValue,
    diffDays,
    adjustmentType: 'none',
    adjustmentAmount: 0,
    appliedRatePct: 0,
    hasAdjustment: false,
    statusMessage,
    badgeText: null,
    tooltip: `Valor: ${formatBRL(baseValue)}`
  };
};

/**
 * Quick helper that returns the calculated final price (float).
 */
export const calculateFinalPrice = (record) => {
  return calculateRecordPriceDetails(record).finalValue;
};
